import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { closerAPI } from '../../utils/api';

const money = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const PERIOD_LABELS = {
  today: 'Hoy',
  week: 'Esta semana',
  month: 'Este mes',
};

// Activaciones y comisión del closer. El corte diario se reinicia cada día
// (hora de México) y la comisión sube por escalones conforme cierra más ventas.
const ActivationsTab = () => {
  const [summary, setSummary] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [activations, setActivations] = useState([]);
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([
      closerAPI.getSummary().catch(() => null),
      closerAPI.getRanking({ period: 'today' }).catch(() => null),
    ]).then(([summaryRes, rankingRes]) => {
      if (!active) return;
      if (summaryRes?.success) setSummary(summaryRes.data);
      if (rankingRes?.success) setRanking(rankingRes.data || []);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    closerAPI
      .getActivations({ period, per_page: 20 })
      .then((res) => {
        if (active && res?.success) setActivations(res.data || []);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [period]);

  const card = {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid rgba(0,0,0,0.06)',
    padding: 18,
  };

  if (loading) {
    return (
      <div style={{ padding: '8px 20px 20px' }}>
        <div style={{ ...card, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
          Cargando tus activaciones...
        </div>
      </div>
    );
  }

  const today = summary?.today || { activations: 0, commission: 0 };
  const nextTier = summary?.next_tier;

  return (
    <div style={{ padding: '8px 20px 20px' }}>
      {/* Hoy — destacado en negro (es el corte que se reinicia diario) */}
      <div style={{
        background: '#0A0A0A', color: '#fff', borderRadius: 24, padding: 22,
      }}>
        <div style={{ fontSize: 13, opacity: 0.6, fontWeight: 500 }}>Activaciones de hoy</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
          <span style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>
            {today.activations}
          </span>
          <span style={{ fontSize: 15, opacity: 0.6 }}>ventas</span>
        </div>

        <div style={{
          marginTop: 16, paddingTop: 14,
          borderTop: '1px solid rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 12.5, opacity: 0.6 }}>Comisión de hoy</div>
            <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {money(today.commission)}
            </div>
          </div>
          {summary?.current_rate != null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12.5, opacity: 0.6 }}>Vas en</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{money(summary.current_rate)} c/u</div>
            </div>
          )}
        </div>

        {nextTier && (
          <div style={{
            marginTop: 14, borderRadius: 14, padding: '11px 14px',
            background: 'rgba(255,255,255,0.1)',
            fontSize: 13.5, fontWeight: 600, lineHeight: 1.4,
          }}>
            Te {nextTier.remaining === 1 ? 'falta' : 'faltan'} {nextTier.remaining}{' '}
            {nextTier.remaining === 1 ? 'venta' : 'ventas'} para subir a {money(nextTier.amount)} por activación
            {/* En modo retroactivo la tarifa nueva repaga TODO el día: es el gancho más fuerte. */}
            {nextTier.total_if_reached != null && (
              <span style={{ display: 'block', marginTop: 4, fontWeight: 500, opacity: 0.75 }}>
                Al llegar, todas tus ventas del día pagan esa tarifa: {money(nextTier.total_if_reached)} en total.
              </span>
            )}
          </div>
        )}
      </div>

      {/*
        La tabla completa de escalones (Iván, 13-ago-2026).

        Arriba ya decía cuánto lleva ganado y cuánto le falta para el siguiente
        tramo, pero no el mapa entero. Sin verlo, el closer no puede decidir si
        le conviene empujar dos ventas más — que es exactamente la decisión que
        estos escalones existen para provocar.
      */}
      {Array.isArray(summary?.tiers) && summary.tiers.length > 0 && (
        <div style={{ ...card, marginTop: 12, padding: 0, overflow: 'hidden' }}>
          <div style={{
            padding: '13px 18px 10px', fontSize: 13, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6E6E77',
          }}>
            Cuánto paga cada venta
          </div>
          {summary.tiers.map((t) => (
            <div
              key={t.desde}
              style={{
                padding: '11px 18px',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                background: t.actual ? 'rgba(250,77,10,0.07)' : 'transparent',
              }}
            >
              <span style={{ fontSize: 14, color: t.actual ? '#0E0E10' : '#6E6E77', fontWeight: t.actual ? 700 : 500 }}>
                {t.hasta ? `Ventas ${t.desde} a ${t.hasta}` : `De la ${t.desde} en adelante`}
                {t.actual && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#FA4D0A', fontWeight: 700 }}>
                    vas aquí
                  </span>
                )}
              </span>
              <span style={{ fontSize: 15, fontWeight: 700, color: t.actual ? '#FA4D0A' : '#0A0A0A' }}>
                {money(t.monto)}
              </span>
            </div>
          ))}
          {summary?.mode === 'retroactive' && (
            <div style={{ padding: '10px 18px 14px', fontSize: 12, color: '#6E6E77', lineHeight: 1.45 }}>
              Al subir de tramo, <strong>todas</strong> tus ventas del día pasan a pagar la tarifa nueva.
            </div>
          )}
        </div>
      )}

      {/* Semana y mes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        {['week', 'month'].map((key) => {
          const data = summary?.[key] || { activations: 0, commission: 0 };
          return (
            <div key={key} style={card}>
              <div style={{ fontSize: 12.5, color: '#6B7280', fontWeight: 500 }}>
                {PERIOD_LABELS[key]}
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em', marginTop: 2 }}>
                {data.activations}
              </div>
              <div style={{ fontSize: 14, color: '#0A0A0A', fontWeight: 600, marginTop: 4 }}>
                {money(data.commission)}
              </div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>de comisión</div>
            </div>
          );
        })}
      </div>

      {/* Ventas recientes */}
      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.01em' }}>
            Tus ventas
          </h3>
          <div style={{ display: 'flex', gap: 5 }}>
            {Object.keys(PERIOD_LABELS).map((key) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                style={{
                  padding: '5px 11px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                  fontSize: 12.5, fontWeight: 600,
                  background: period === key ? '#0A0A0A' : '#fff',
                  color: period === key ? '#fff' : '#6B7280',
                  boxShadow: period === key ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                {PERIOD_LABELS[key]}
              </button>
            ))}
          </div>
        </div>

        {activations.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
            Todavía no hay ventas en este periodo.
          </div>
        ) : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {activations.map((a, i) => (
              <div
                key={a.id}
                style={{
                  padding: '14px 18px',
                  borderTop: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 14.5, fontWeight: 600, color: '#0A0A0A',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.customer_name}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#9CA3AF', marginTop: 1 }}>
                    {new Date(a.activated_at).toLocaleString('es-MX', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0A0A0A', flexShrink: 0 }}>
                  {money(a.commission_amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ranking (el admin puede ocultarlo; llega vacío si está apagado) */}
      {ranking.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <h3 style={{
            fontSize: 16, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.01em',
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <Trophy size={16} color="#6B7280" /> Ranking de hoy
          </h3>
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {ranking.map((r, i) => (
              <div
                key={r.closer_id}
                style={{
                  padding: '13px 18px',
                  borderTop: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                <span style={{
                  width: 24, fontSize: 13, fontWeight: 700,
                  color: i === 0 ? '#0A0A0A' : '#9CA3AF',
                }}>
                  {i + 1}º
                </span>
                <span style={{
                  flex: 1, fontSize: 14.5, fontWeight: 600, color: '#0A0A0A',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {r.name}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0A0A0A' }}>
                  {r.activations}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationsTab;
